import { useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  UserDisplayPreferences,
  DEFAULT_DISPLAY_PREFERENCES,
} from '../types/userPreferences';
import {
  formatDate as formatDateUtil,
  formatTime as formatTimeUtil,
  formatDateTime as formatDateTimeUtil,
  formatRelativeTime as formatRelativeTimeUtil,
  formatTimeString as formatTimeStringUtil,
  formatCurrency as formatCurrencyUtil,
  formatLength as formatLengthUtil,
  formatDimensions as formatDimensionsUtil,
  formatTonnage as formatTonnageUtil,
  formatSpeed as formatSpeedUtil,
  formatNumber as formatNumberUtil,
  getCalendarType as getCalendarTypeUtil,
} from '../utils/formatUtils';

/**
 * Hook that provides access to user display preferences and formatting functions.
 * All formatting functions are memoized based on the user's preferences.
 */
export function useDisplayPreferences() {
  const { displayPreferences, updateUserDisplayPreferences } = useAuth();

  // Memoize the preferences to avoid unnecessary re-renders
  const preferences = useMemo<UserDisplayPreferences>(
    () => displayPreferences || DEFAULT_DISPLAY_PREFERENCES,
    [displayPreferences]
  );

  // Memoized formatting functions that use current preferences
  const formatDate = useCallback(
    (
      date: Date | string,
      options?: {
        includeWeekday?: boolean;
        shortFormat?: boolean;
        fullMonth?: boolean;
      }
    ) => formatDateUtil(date, preferences, options),
    [preferences]
  );

  const formatTime = useCallback(
    (date: Date | string) => formatTimeUtil(date, preferences),
    [preferences]
  );

  const formatDateTime = useCallback(
    (date: Date | string) => formatDateTimeUtil(date, preferences),
    [preferences]
  );

  const formatRelativeTime = useCallback(
    (date: Date | string) => formatRelativeTimeUtil(date),
    []
  );

  const formatTimeString = useCallback(
    (timeString: string | undefined | null) => formatTimeStringUtil(timeString, preferences),
    [preferences]
  );

  const formatCurrency = useCallback(
    (amount: number | undefined | null) => formatCurrencyUtil(amount, preferences),
    [preferences]
  );

  const formatLength = useCallback(
    (value: string | number | undefined | null) => formatLengthUtil(value, preferences),
    [preferences]
  );

  const formatDimensions = useCallback(
    (
      length: string | number | undefined | null,
      width: string | number | undefined | null
    ) => formatDimensionsUtil(length, width, preferences),
    [preferences]
  );

  const formatTonnage = useCallback(
    (value: string | number | undefined | null) => formatTonnageUtil(value),
    []
  );

  const formatSpeed = useCallback(
    (value: string | number | undefined | null) => formatSpeedUtil(value, preferences),
    [preferences]
  );

  const formatNumber = useCallback(
    (value: string | number | undefined | null) => formatNumberUtil(value),
    []
  );

  const getCalendarType = useCallback(
    () => getCalendarTypeUtil(preferences),
    [preferences]
  );

  return {
    // Current preferences
    preferences,
    
    // Update function
    updatePreferences: updateUserDisplayPreferences,
    
    // Formatting functions
    formatDate,
    formatTime,
    formatDateTime,
    formatRelativeTime,
    formatTimeString,
    formatCurrency,
    formatLength,
    formatDimensions,
    formatTonnage,
    formatSpeed,
    formatNumber,
    getCalendarType,
  };
}

export default useDisplayPreferences;
